import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  ArrowLeft, List, Sun, Moon, Type, Bookmark, BookmarkCheck,
  Download, Search, X, Loader2, ChevronLeft, ChevronRight,
} from "lucide-react";
import RichText from "@/components/RichText";

const FONT_SIZES = [15, 17, 19, 21, 24];

const Reader = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [dark, setDark] = useState(() => localStorage.getItem("reader-dark") === "1");
  const [fontIdx, setFontIdx] = useState(() => parseInt(localStorage.getItem("reader-font") || "1", 10));
  const [active, setActive] = useState(0);
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [bookmarks, setBookmarks] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`bm-${slug}`) || "[]"); } catch { return []; }
  });
  const [downloading, setDownloading] = useState(false);
  const sectionRefs = useRef([]);

  const { data: g, isLoading, isError } = useQuery({
    queryKey: ["guide", slug],
    queryFn: async () => (await api.get(`/guides/${slug}`)).data,
    enabled: !authLoading,
  });

  useEffect(() => { localStorage.setItem("reader-dark", dark ? "1" : "0"); }, [dark]);
  useEffect(() => { localStorage.setItem("reader-font", String(fontIdx)); }, [fontIdx]);
  useEffect(() => { localStorage.setItem(`bm-${slug}`, JSON.stringify(bookmarks)); }, [bookmarks, slug]);

  useEffect(() => {
    if (!authLoading && !user) navigate("/");
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (g && !g.entitled) {
      toast.error("Purchase this guide to read it");
      navigate(`/guides/${slug}`);
    }
  }, [g, slug, navigate]);

  const searchResults = useMemo(() => {
    if (!search.trim() || !g) return [];
    const q = search.toLowerCase();
    return g.sections
      .map((s, i) => ({ i, title: s.title, hit: s.title.toLowerCase().includes(q) || s.content.toLowerCase().includes(q) }))
      .filter((r) => r.hit);
  }, [search, g]);

  const scrollTo = (i) => {
    setActive(i);
    sectionRefs.current[i]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const toggleBookmark = (i) => {
    setBookmarks((b) => (b.includes(i) ? b.filter((x) => x !== i) : [...b, i]));
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/guides/${slug}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `UMoooV-${g.country}-Guide.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  if (authLoading || isLoading || !g) {
    return <div className="min-h-screen flex justify-center items-center bg-[#F9F9F7]"><Loader2 className="w-8 h-8 animate-spin text-[#9A3412]" /></div>;
  }
  if (isError) return <div className="min-h-screen flex justify-center items-center">Unable to load guide.</div>;

  const bg = dark ? "bg-stone-950" : "bg-[#F9F9F7]";
  const surface = dark ? "bg-stone-900" : "bg-white";
  const textMain = dark ? "text-stone-200" : "text-stone-800";
  const textMuted = dark ? "text-stone-400" : "text-stone-500";
  const border = dark ? "border-stone-800" : "border-stone-200";
  const navBar = dark ? "bg-stone-950/90 border-stone-800" : "bg-white/90 border-stone-200";

  const TOC = () => (
    <nav className="space-y-1">
      {g.sections.map((s, i) => (
        <button
          key={i}
          data-testid={`toc-item-${i}`}
          onClick={() => scrollTo(i)}
          className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2 ${
            active === i ? "text-[#9A3412] font-medium" : `${textMuted} hover:${dark ? "text-stone-200" : "text-stone-900"}`
          }`}
        >
          <span className="opacity-50 tabular-nums">{i + 1}</span>
          <span className="flex-1 line-clamp-2">{s.title}</span>
          {bookmarks.includes(i) && <BookmarkCheck className="w-3.5 h-3.5 text-[#9A3412]" />}
        </button>
      ))}
    </nav>
  );

  return (
    <div className={`min-h-screen ${bg} transition-colors`}>
      {/* Top bar */}
      <div className={`fixed top-0 w-full ${navBar} backdrop-blur-xl border-b z-50`}>
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-14 flex items-center gap-2">
          <Button data-testid="reader-back-btn" variant="ghost" size="icon" onClick={() => navigate(`/guides/${slug}`)} className={textMain}><ArrowLeft className="w-5 h-5" /></Button>

          <Sheet>
            <SheetTrigger asChild>
              <Button data-testid="reader-toc-btn" variant="ghost" size="icon" className={`lg:hidden ${textMain}`}><List className="w-5 h-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className={dark ? "bg-stone-950 border-stone-800" : ""}>
              <SheetHeader><SheetTitle className={`font-serif text-2xl ${textMain}`}>{g.country}</SheetTitle></SheetHeader>
              <div className="mt-6"><TOC /></div>
            </SheetContent>
          </Sheet>

          <span className={`flex items-center gap-2 font-serif text-lg ${textMain} truncate`}>
            <span>{g.flag}</span><span className="truncate">{g.country}</span>
          </span>

          <div className="flex-1" />

          {showSearch && (
            <div className="relative">
              <Input
                data-testid="reader-search-input"
                autoFocus
                placeholder="Search chapters…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`w-40 md:w-56 h-9 rounded-none ${dark ? "bg-stone-900 border-stone-700 text-stone-200" : ""}`}
              />
              {searchResults.length > 0 && (
                <div className={`absolute top-11 right-0 w-64 ${surface} border ${border} shadow-lg z-50`}>
                  {searchResults.map((r) => (
                    <button key={r.i} data-testid={`search-result-${r.i}`} onClick={() => { scrollTo(r.i); setShowSearch(false); setSearch(""); }} className={`block w-full text-left px-4 py-2 text-sm ${textMain} hover:bg-stone-500/10`}>{r.i + 1}. {r.title}</button>
                  ))}
                </div>
              )}
            </div>
          )}
          <Button data-testid="reader-search-btn" variant="ghost" size="icon" onClick={() => { setShowSearch(!showSearch); setSearch(""); }} className={textMain}>
            {showSearch ? <X className="w-5 h-5" /> : <Search className="w-5 h-5" />}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="reader-font-btn" variant="ghost" size="icon" className={textMain}><Type className="w-5 h-5" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="p-4 w-52">
              <p className="text-xs uppercase tracking-widest text-stone-500 mb-3">Text size</p>
              <div className="flex items-center justify-between gap-2">
                <Button data-testid="font-decrease" variant="outline" size="icon" className="rounded-none" disabled={fontIdx === 0} onClick={() => setFontIdx((i) => Math.max(0, i - 1))}><ChevronLeft className="w-4 h-4" /></Button>
                <span className="text-sm tabular-nums">{FONT_SIZES[fontIdx]}px</span>
                <Button data-testid="font-increase" variant="outline" size="icon" className="rounded-none" disabled={fontIdx === FONT_SIZES.length - 1} onClick={() => setFontIdx((i) => Math.min(FONT_SIZES.length - 1, i + 1))}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button data-testid="reader-dark-toggle" variant="ghost" size="icon" onClick={() => setDark(!dark)} className={textMain}>
            {dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>

          <Button data-testid="reader-download-btn" variant="ghost" size="icon" onClick={downloadPdf} disabled={downloading} className={textMain}>
            {downloading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-6xl mx-auto pt-14 flex">
        {/* Desktop TOC */}
        <aside className={`hidden lg:block w-72 shrink-0 border-r ${border} h-[calc(100vh-3.5rem)] sticky top-14 overflow-y-auto reader-scroll py-8 px-4`}>
          <p className={`text-xs uppercase tracking-widest ${textMuted} mb-4 px-3`}>Contents</p>
          <TOC />
          {bookmarks.length > 0 && (
            <div className="mt-8 px-3">
              <p className={`text-xs uppercase tracking-widest ${textMuted} mb-3`}>Bookmarks</p>
              {bookmarks.sort((a, b) => a - b).map((i) => (
                <button key={i} onClick={() => scrollTo(i)} className={`block text-left text-sm ${textMuted} hover:text-[#9A3412] py-1`}>{i + 1}. {g.sections[i].title}</button>
              ))}
            </div>
          )}
        </aside>

        {/* Content */}
        <article className="flex-1 max-w-2xl mx-auto px-6 py-12 md:py-16">
          <p className={`text-sm uppercase tracking-widest ${textMuted} mb-3`}>UMoooV Relocation Guide</p>
          <h1 className={`font-serif text-5xl md:text-6xl font-medium ${dark ? "text-white" : "text-stone-900"} mb-2`}>{g.country} {g.flag}</h1>
          <p className={`${textMuted} mb-4`}>{g.tagline}</p>
          <p className={`text-xs ${textMuted} mb-12`}>Last updated {g.last_updated} · Informational draft, not legal/tax/immigration advice.</p>

          {g.key_facts && (
            <div data-testid="reader-key-facts" className={`grid grid-cols-2 md:grid-cols-4 gap-4 mb-14 border-y ${border} py-6`}>
              {[
                ["Language", g.key_facts.language],
                ["Best visa", g.key_facts.best_visa],
                ["Residency", g.key_facts.residency],
                ["Verdict", g.key_facts.verdict],
              ].map(([label, val], i) => (
                <div key={i}>
                  <p className={`text-xs uppercase tracking-wider ${textMuted} mb-1`}>{label}</p>
                  <p className={`text-sm ${textMain}`}>{val}</p>
                </div>
              ))}
            </div>
          )}

          {g.sections.map((s, i) => (
            <section
              key={i}
              data-testid={`reader-section-${i}`}
              ref={(el) => (sectionRefs.current[i] = el)}
              className="mb-14 scroll-mt-20"
              onMouseEnter={() => setActive(i)}
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <h2 className={`font-serif text-3xl font-medium ${dark ? "text-stone-100" : "text-stone-900"}`}>
                  <span className="text-[#9A3412] mr-2">{i + 1}.</span>{s.title}
                </h2>
                <button data-testid={`bookmark-btn-${i}`} onClick={() => toggleBookmark(i)} className="mt-2 shrink-0">
                  {bookmarks.includes(i) ? <BookmarkCheck className="w-5 h-5 text-[#9A3412]" /> : <Bookmark className={`w-5 h-5 ${textMuted} hover:text-[#9A3412]`} />}
                </button>
              </div>
              <div className={`font-reader ${textMain} leading-[1.85]`} style={{ fontSize: `${FONT_SIZES[fontIdx]}px` }}>
                <RichText text={s.content} headingClass={dark ? "text-stone-100" : "text-stone-900"} />
              </div>
            </section>
          ))}

          <div className={`border-t ${border} pt-8 text-xs ${textMuted}`}>
            Disclaimer: Visa, tax, and residency rules change frequently. Always verify with official government sources and qualified professionals. © UMoooV.
          </div>
        </article>
      </div>
    </div>
  );
};

export default Reader;
